<template>
  <iframe
    :src="url"
    :style="style"
    @load="iframeLoaded()"
  />
</template>

<script>
import _uniqueId from 'lodash/uniqueId'
import _get from 'lodash/get'
import _debounce from 'lodash/debounce'
import qs from 'qs'

export default {
  props: {
    language: {
      type: String,
      default: 'json'
    },
    value: {
      required: true,
      type: String
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    focusOnLoaded: {
      type: Boolean,
      default: false
    },
    minimap: {
      type: Boolean,
      default: true
    },
    lineNumbers: {
      type: String,
      default: 'on'
    },
    wordWrap: {
      type: String,
      default: 'off'
    },
    codeCopyMode: {
      type: Boolean,
      default: false
    },
    // Tables of the project for the SQL schema-aware completion:
    // [{ name, database, columns: [{ name, type }] }] (see helpers/SqlMetadata.js).
    sqlMetadata: {
      type: Array,
      default: null
    }
  },
  data () {
    return {
      uniqueId: _uniqueId()
    }
  },
  computed: {
    style () {
      if (this.$store.getters.RESIZING) {
        return {
          'pointer-events': 'none'
        }
      }
      return {}
    },
    queryString () {
      return {
        uniqueId: this.uniqueId,
        lang: this.$i18n.locale(),
        language: this.language,
        readOnly: this.readOnly,
        minimap: this.minimap,
        lineNumbers: this.lineNumbers,
        wordWrap: this.wordWrap,
        codeCopyMode: this.codeCopyMode,
        // Files under /vs/ have no content hashes: the app version in the URL
        // makes browsers fetch a fresh iframe page after each release.
        v: process.env.VUE_APP_VERSION
      }
    },
    url () {
      return `vs/Iframe.html?${qs.stringify(this.queryString)}`
    }
  },
  watch: {
    value (val) {
      this.updateIframe(val)
    },
    sqlMetadata () {
      this.sendSqlMetadata()
    }
  },
  created () {
    this.updateIframe = _debounce(this.updateIframe, 100)
    window.addEventListener('message', (event) => {
      if (_get(event, 'data.type') !== 'MonacoIframe') return
      if (_get(event, 'data.id') !== this.uniqueId) return

      // Custom binding cmd/Ctrl + Enter
      if (event.data.name === 'custom-run') {
        this.$emit('customRun')
        return
      }

      // Custom binding cmd/Ctrl + S, forwarded because the iframe holds focus.
      if (event.data.name === 'custom-save') {
        this.$emit('customSave')
        return
      }

      this[event.data.name](event.data)
    }, false)
  },
  methods: {
    iframeLoaded () {
      this.$emit('loaded')
      if (this.focusOnLoaded) this.focus()
    },
    focus () {
      this.$el.contentWindow.focus()
      this.$el.contentWindow.postMessage({ event: 'focus' }, window.location.origin)
    },
    updateIframe (val) {
      if (this.$el.contentWindow) {
        this.$el.contentWindow.postMessage({ event: 'content', content: val }, window.location.origin)
      }
    },
    sendSqlMetadata () {
      if (!this.$el.contentWindow) return
      // Vuex reactive proxies fail postMessage's structured clone: send a plain copy.
      const tables = this.sqlMetadata ? JSON.parse(JSON.stringify(this.sqlMetadata)) : null
      this.$el.contentWindow.postMessage({ event: 'metadata', tables }, window.location.origin)
    },
    ready () {
      this.updateIframe(this.value)
      if (this.sqlMetadata) this.sendSqlMetadata()
    },
    input (event) {
      this.$emit('input', event.data)
    }
  }
}
</script>

<style lang='less'>
iframe {
  width:100%;
  border:0;
  height: 100%;
}
</style>
